import * as THREE from "three";
import { GROUND, LAND } from "@somnhold/content";
import {
  buildTerrain,
  cellId,
  DIRS,
  gridW,
  hexLen,
  hexToWorld,
  landStep,
  type LandDef,
  type MapDef,
} from "@somnhold/sim";
import { WORLD_PER_HEX } from "./coords.ts";
import { FogBank } from "./outskirts.ts";
import { landMaterial } from "./landshade.ts";
import { mulberry, type TerrainBuild } from "./terrain.ts";

/**
 * The bastion from scratch (docs/CONSTRUCCION.md) as a diorama: terraced hex land, each cell on its level.
 * Where two terraces meet by a ramp the tops lean into each other; where they meet by a cliff a wall of
 * banded rock drops between them. Rivers and lakes lie in sunken beds under a still sheet of water, fords
 * show their gravel through it. Woods, rock outcrops and the ruins of the old garrison are a few instanced
 * meshes. One merged mesh and no new lights (docs/RENDIMIENTO.md). Render-only: the sim never reads this.
 */

const S = WORLD_PER_HEX;
/** World units per level of height. */
export const LEVEL_H = 0.8;
/** Height of the crest the keep stands on. */
export const LAND_CREST_Y = LEVEL_H * LAND.top;
/** How far the table's skirt drops under the lowest ground. */
const BASE = -1.4;
const RAD = S / Math.sqrt(3);

/** Corner `j` of a pointy hex sits at angle 60j − 30 degrees (as in terrain.ts). */
const CORNER: [number, number][] = [];
for (let j = 0; j < 6; j++) {
  const a = (Math.PI / 180) * (60 * j - 30);
  CORNER.push([Math.cos(a) * RAD, Math.sin(a) * RAD]);
}
const DIR_DEG = DIRS.map((d) => {
  const w = hexToWorld(d.q, d.r);
  return (Math.atan2(w.y, w.x) * 180) / Math.PI;
});
/** The neighbour (DIRS index) across edge j (between corners j and j + 1), and the two sharing corner j. */
const EDGE_NB: number[] = [];
const CORNER_NB: [number, number][] = [];
for (let j = 0; j < 6; j++) {
  const edgeDeg = 60 * j;
  const cornerDeg = 60 * j - 30;
  const gap = (a: number, b: number): number =>
    Math.abs(((((a - b) % 360) + 540) % 360) - 180);
  EDGE_NB.push(DIR_DEG.findIndex((d) => gap(d, edgeDeg) < 1));
  const two = DIR_DEG.map((d, k) => ({ k, g: gap(d, cornerDeg) }))
    .filter((x) => x.g < 31)
    .map((x) => x.k);
  CORNER_NB.push([two[0] as number, two[1] as number]);
}

const C = {
  grassA: new THREE.Color("#38412c"),
  grassB: new THREE.Color("#414a32"),
  heath: new THREE.Color("#4a4639"),
  moss: new THREE.Color("#2f4429"),
  floor: new THREE.Color("#232b1d"),
  ruin: new THREE.Color("#4f5250"),
  plaza: new THREE.Color("#7d8084"),
  rockTop: new THREE.Color("#57595c"),
  cliff: new THREE.Color("#45474c"),
  cliffDark: new THREE.Color("#34353a"),
  earth: new THREE.Color("#3b3428"),
  gravel: new THREE.Color("#6a6352"),
  /** A trench the group dug: dark turned earth. */
  ditch: new THREE.Color("#2e2618"),
  bed: new THREE.Color("#1c272b"),
  skirt: new THREE.Color("#202125"),
  water: new THREE.Color("#1f3b48"),
};

const hash2 = (q: number, r: number): number => {
  let h = Math.imul(q * 374761393 + r * 668265263, 0x27d4eb2d);
  h = (h ^ (h >>> 15)) >>> 0;
  return h / 4294967296;
};

export interface LandBuild extends TerrainBuild {
  /** Height of each corner of each cell's top (id·6 + j). */
  corners: Float32Array;
  water: THREE.Mesh;
}

const isGround = (k: number): boolean =>
  k !== GROUND.rock && k !== GROUND.water && k !== GROUND.vein;

/** Height of a cell's centre: its level, the bed of water sunk below it, rock standing up. */
const topOf = (land: LandDef, id: number): number => {
  const k = land.kind[id] as number;
  const base = (land.level[id] as number) * LEVEL_H;
  if (k === GROUND.water) return base - 0.34;
  if (k === GROUND.ford) return base - 0.14;
  if (k === GROUND.ditch) return base - 0.18;
  if (k === GROUND.rock || k === GROUND.vein) return base + 0.28;
  return base;
};

/**
 * The land as a diorama. With `reuse` (an earthwork changed the land), its materials, the table and the fog bank
 * are kept and only the geometry is new: no shader is compiled again and nothing but the shapes is uploaded.
 */
export const buildLandMesh = (
  map: MapDef,
  seed: number,
  opts: { low?: boolean; reuse?: LandBuild } = {},
): LandBuild => {
  const land = map.land as LandDef;
  const R = map.radius;
  const W = gridW(R);
  const terrain = buildTerrain(map);
  const heights = new Float32Array(W * W);
  const corners = new Float32Array(W * W * 6);
  const ids: number[] = [];
  for (let r = -R; r <= R; r++)
    for (let q = -R; q <= R; q++)
      if (hexLen(q, r) <= R) ids.push(cellId(R, q, r));
  const nbOf = (id: number, k: number): number => {
    const q = (id % W) - R;
    const r = Math.floor(id / W) - R;
    return cellId(
      R,
      q + (DIRS[k] as { q: number }).q,
      r + (DIRS[k] as { r: number }).r,
    );
  };
  // Corners: a ground cell's top leans toward the neighbours it is joined to (same level or a ramp).
  for (const id of ids) {
    const h = topOf(land, id);
    heights[id] = h;
    const k = land.kind[id] as number;
    for (let j = 0; j < 6; j++) {
      let sum = h;
      let n = 1;
      if (isGround(k)) {
        for (const d of CORNER_NB[j] as [number, number]) {
          const o = nbOf(id, d);
          if (
            o < 0 ||
            !isGround(land.kind[o] as number) ||
            landStep(land, id, o) < 0
          )
            continue;
          sum += topOf(land, o);
          n++;
        }
      }
      corners[id * 6 + j] = sum / n;
    }
  }

  // Written into typed arrays that grow by doubling: no spread and no per-vertex garbage (a rebuild after an
  // earthwork runs on the board, docs/RENDIMIENTO.md).
  let pos = new Float32Array(ids.length * 9 * 14);
  let col = new Float32Array(pos.length);
  let n9 = 0;
  const tri = (
    a: number[],
    b: number[],
    c: number[],
    ca: THREE.Color,
    cb: THREE.Color,
    cc: THREE.Color,
  ): void => {
    if (n9 + 9 > pos.length) {
      const p2 = new Float32Array(pos.length * 2);
      p2.set(pos);
      pos = p2;
      const c2 = new Float32Array(col.length * 2);
      c2.set(col);
      col = c2;
    }
    pos[n9] = a[0] as number;
    pos[n9 + 1] = a[1] as number;
    pos[n9 + 2] = a[2] as number;
    pos[n9 + 3] = b[0] as number;
    pos[n9 + 4] = b[1] as number;
    pos[n9 + 5] = b[2] as number;
    pos[n9 + 6] = c[0] as number;
    pos[n9 + 7] = c[1] as number;
    pos[n9 + 8] = c[2] as number;
    col[n9] = ca.r;
    col[n9 + 1] = ca.g;
    col[n9 + 2] = ca.b;
    col[n9 + 3] = cb.r;
    col[n9 + 4] = cb.g;
    col[n9 + 5] = cb.b;
    col[n9 + 6] = cc.r;
    col[n9 + 7] = cc.g;
    col[n9 + 8] = cc.b;
    n9 += 9;
  };
  const wpos: number[] = [];
  const tmp = new THREE.Color();
  const edgeC = new THREE.Color();
  const wallA = new THREE.Color();
  const wallB = new THREE.Color();
  for (const id of ids) {
    const q = (id % W) - R;
    const r = Math.floor(id / W) - R;
    const c = hexToWorld(q, r);
    const cx = (c.x / 1000) * S;
    const cz = (c.y / 1000) * S;
    const k = land.kind[id] as number;
    const lv = land.level[id] as number;
    const n = hash2(q, r);
    const d = hexLen(q, r);
    let top: THREE.Color;
    if (d <= 1) top = tmp.copy(C.plaza).multiplyScalar(0.92 + n * 0.12);
    else if (k === GROUND.wood) top = tmp.copy(C.floor).lerp(C.moss, n * 0.5);
    else if (k === GROUND.rock)
      top = tmp.copy(C.rockTop).multiplyScalar(0.9 + n * 0.2);
    else if (k === GROUND.vein)
      top = tmp.copy(C.rockTop).multiplyScalar(0.72 + n * 0.12);
    else if (k === GROUND.ruin)
      top = tmp
        .copy(C.ruin)
        .lerp(C.grassA, 0.25 + n * 0.35)
        .multiplyScalar(0.92 + n * 0.1);
    else if (k === GROUND.ford)
      top = tmp.copy(C.gravel).multiplyScalar(0.9 + n * 0.15);
    else if (k === GROUND.ditch)
      top = tmp.copy(C.ditch).multiplyScalar(0.9 + n * 0.15);
    else if (k === GROUND.water) top = tmp.copy(C.bed);
    // Grass, drier and stonier the higher it stands.
    else
      top = tmp
        .copy(n < 0.5 ? C.grassA : C.grassB)
        .lerp(C.heath, lv * 0.14 + n * 0.12);
    // The rim of each tile a touch darker; the grid itself is a fine line drawn in the shader (landshade.ts).
    edgeC.copy(top).multiplyScalar(k === GROUND.water ? 1 : 0.95);
    const h = heights[id] as number;
    const center = [cx, h, cz];
    for (let j = 0; j < 6; j++) {
      const j2 = (j + 1) % 6;
      const a = [
        cx + (CORNER[j] as [number, number])[0],
        corners[id * 6 + j] as number,
        cz + (CORNER[j] as [number, number])[1],
      ];
      const b = [
        cx + (CORNER[j2] as [number, number])[0],
        corners[id * 6 + j2] as number,
        cz + (CORNER[j2] as [number, number])[1],
      ];
      tri(center, b, a, top, edgeC, edgeC);
      // Water: a still sheet over the bed (and thin over a ford's gravel).
      if (k === GROUND.water || k === GROUND.ford) {
        const wy = lv * LEVEL_H - (k === GROUND.water ? 0.1 : 0.06);
        wpos.push(
          cx,
          wy,
          cz,
          b[0] as number,
          wy,
          b[2] as number,
          a[0] as number,
          wy,
          a[2] as number,
        );
      }
      // The side toward the neighbour across this edge, wherever this top stands above its own.
      const o = nbOf(id, EDGE_NB[j] as number);
      let ga = BASE;
      let gb = BASE;
      if (o >= 0) {
        // The neighbour's corners at the same two points: across edge j, corner j is its corner j + 4 and j + 1 its j + 3.
        ga = corners[o * 6 + ((j + 4) % 6)] as number;
        gb = corners[o * 6 + ((j + 3) % 6)] as number;
      }
      if ((a[1] as number) <= ga + 1e-3 && (b[1] as number) <= gb + 1e-3)
        continue;
      const ok = o >= 0 ? (land.kind[o] as number) : -1;
      const sheer =
        o < 0 ||
        k === GROUND.rock ||
        k === GROUND.vein ||
        (isGround(k) && ok >= 0 && isGround(ok) && landStep(land, id, o) < 0);
      // Banded rock down a cliff; earth down a bank or a ramp's lip.
      if (sheer) {
        wallA.copy(C.cliff).multiplyScalar(0.92 + n * 0.16);
        wallB.copy(C.cliffDark);
      } else {
        wallA.copy(C.earth).multiplyScalar(0.95 + n * 0.1);
        wallB.copy(C.earth).multiplyScalar(0.7);
      }
      if (o < 0) wallB.copy(C.skirt);
      const a2 = [a[0] as number, Math.min(a[1] as number, ga), a[2] as number];
      const b2 = [b[0] as number, Math.min(b[1] as number, gb), b[2] as number];
      tri(a, b, b2, wallA, wallA, wallB);
      tri(a, b2, a2, wallA, wallB, wallB);
      // A cliff two levels high gets a ledge of lighter rock halfway down.
      if (sheer && o >= 0 && (a[1] as number) - ga > LEVEL_H * 1.5) {
        const mid = (y1: number, y2: number): number => (y1 + y2) / 2;
        const ma = [
          a[0] as number,
          mid(a[1] as number, a2[1] as number),
          a[2] as number,
        ];
        const mb = [
          b[0] as number,
          mid(b[1] as number, b2[1] as number),
          b[2] as number,
        ];
        const ledge = wallA.clone().multiplyScalar(1.12);
        tri(
          ma,
          mb,
          [mb[0] as number, (mb[1] as number) - 0.04, mb[2] as number],
          ledge,
          ledge,
          ledge,
        );
      }
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute(
    "position",
    new THREE.BufferAttribute(pos.subarray(0, n9), 3),
  );
  geo.setAttribute("color", new THREE.BufferAttribute(col.subarray(0, n9), 3));
  // Flat shaded: the shader takes each face's normal from its own slope, so no normals are worked out here.
  geo.computeBoundingSphere();
  const old = opts.reuse;
  const mesh = new THREE.Mesh(
    geo,
    old
      ? (old.mesh.material as THREE.Material)
      : landMaterial(opts.low ?? false),
  );
  mesh.name = "land";
  mesh.receiveShadow = true;

  const wgeo = new THREE.BufferGeometry();
  wgeo.setAttribute("position", new THREE.Float32BufferAttribute(wpos, 3));
  wgeo.computeVertexNormals();
  const water = new THREE.Mesh(
    wgeo,
    old
      ? (old.water.material as THREE.Material)
      : new THREE.MeshStandardMaterial({
          color: C.water,
          roughness: 0.55,
          metalness: 0,
          transparent: true,
          opacity: 0.82,
          depthWrite: false,
        }),
  );
  water.name = "land-water";
  water.renderOrder = 1;

  const decor = buildDecor(
    map,
    land,
    ids,
    heights,
    seed,
    opts.low ?? false,
    old?.decor ?? null,
  );
  decor.add(water);
  // The table under the diorama, so the rim's skirt stands on something (kept as it was on a rebuild).
  const oldTable = old?.decor.getObjectByName("land-table");
  const table =
    oldTable ??
    new THREE.Mesh(
      new THREE.CircleGeometry((R + 3) * S, 48).rotateX(-Math.PI / 2),
      new THREE.MeshStandardMaterial({ color: "#17181b", roughness: 1 }),
    );
  table.name = "land-table";
  table.position.y = BASE - 0.01;
  decor.add(table);
  // The rim fades into the mist, as the field round the old bastion does (outskirts.ts).
  const rim = R * S * 0.866;
  const fog =
    old?.fog ?? new FogBank([], rim - 6, rim + 4, opts.low ?? false, seed);
  return { mesh, terrain, heights, decor, corners, water, fog };
};

/** Woods, rock outcrops, the ruins' broken stones and tufts on open ground, instanced by kind. */
const buildDecor = (
  map: MapDef,
  land: LandDef,
  ids: number[],
  heights: Float32Array,
  seed: number,
  low: boolean,
  old: THREE.Group | null,
): THREE.Group => {
  const g = new THREE.Group();
  g.name = "land-decor";
  const R = map.radius;
  const W = gridW(R);
  // Each cell draws from its own seed, so clearing one wood leaves every other tree where it stood.
  let rnd = mulberry(seed ^ 0x1a4d);
  type Item = { m: THREE.Matrix4; c: THREE.Color };
  const trunks: Item[] = [];
  const cones: Item[] = [];
  const rocks: Item[] = [];
  const stones: Item[] = [];
  const tufts: Item[] = [];
  const shards: Item[] = [];
  const q4 = new THREE.Quaternion();
  const e = new THREE.Euler();
  const put = (
    list: Item[],
    x: number,
    y: number,
    z: number,
    sx: number,
    sy: number,
    sz: number,
    color: THREE.Color,
    tilt = 0.1,
    yaw = rnd() * Math.PI * 2,
  ): void => {
    e.set((rnd() - 0.5) * tilt, yaw, (rnd() - 0.5) * tilt);
    q4.setFromEuler(e);
    list.push({
      m: new THREE.Matrix4().compose(
        new THREE.Vector3(x, y, z),
        q4,
        new THREE.Vector3(sx, sy, sz),
      ),
      c: color,
    });
  };
  const tint = (hex: string, v = 0.18): THREE.Color =>
    new THREE.Color(hex).multiplyScalar(1 - v / 2 + rnd() * v);
  const pine = ["#24392a", "#2a4030", "#2f4a2a", "#22352c", "#34452c"];
  for (const id of ids) {
    const q = (id % W) - R;
    const r = Math.floor(id / W) - R;
    const c = hexToWorld(q, r);
    const cx = (c.x / 1000) * S;
    const cz = (c.y / 1000) * S;
    const h = heights[id] as number;
    const k = land.kind[id] as number;
    rnd = mulberry(
      (seed ^
        0x1a4d ^
        Math.imul(q + 4096, 73856093) ^
        Math.imul(r + 4096, 19349663)) >>>
        0,
    );
    const jit = (f: number): number => (rnd() - 0.5) * S * f;
    if (k === GROUND.wood) {
      const n = low ? 2 : 3;
      for (let i = 0; i < n; i++) {
        const x = cx + jit(0.75);
        const z = cz + jit(0.75);
        const tall = 1.0 + rnd() * 0.8;
        const w = 0.3 + rnd() * 0.1;
        const cc = tint(pine[Math.floor(rnd() * pine.length)] as string);
        put(trunks, x, h + 0.15, z, 0.07, 0.4, 0.07, tint("#3e2c1e"), 0.05);
        put(
          cones,
          x,
          h + 0.3 + tall * 0.3,
          z,
          w * 1.15,
          tall * 0.62,
          w * 1.15,
          cc,
          0.06,
        );
        put(
          cones,
          x,
          h + 0.3 + tall * 0.62,
          z,
          w * 0.85,
          tall * 0.5,
          w * 0.85,
          cc,
          0.06,
        );
      }
    } else if (k === GROUND.rock) {
      // A crag: one big boulder and a few smaller round it.
      const s = 0.38 + rnd() * 0.18;
      put(
        rocks,
        cx + jit(0.2),
        h + s * 0.2,
        cz + jit(0.2),
        s * 1.2,
        s * (0.9 + rnd() * 0.5),
        s,
        tint("#4f5153", 0.2),
        0.5,
      );
      for (let i = 0; i < (low ? 1 : 3); i++) {
        const t = 0.14 + rnd() * 0.14;
        put(
          rocks,
          cx + jit(0.8),
          h,
          cz + jit(0.8),
          t * 1.3,
          t,
          t,
          tint("#46484a", 0.2),
          0.6,
        );
      }
    } else if (k === GROUND.vein) {
      // A crystal vein: dark rock split open, a cluster of pale crystal standing out of it.
      const s = 0.34 + rnd() * 0.1;
      put(
        rocks,
        cx + jit(0.2),
        h + s * 0.15,
        cz + jit(0.2),
        s * 1.3,
        s * 0.8,
        s * 1.1,
        tint("#34363a", 0.15),
        0.5,
      );
      for (let i = 0; i < (low ? 4 : 7); i++) {
        const a = rnd() * Math.PI * 2;
        const d = rnd() * S * 0.3;
        const t = 0.32 + rnd() * 0.4;
        put(
          shards,
          cx + Math.cos(a) * d,
          h + s * 0.35 + t * 0.4,
          cz + Math.sin(a) * d,
          0.07 + rnd() * 0.05,
          t,
          0.07 + rnd() * 0.05,
          tint(i % 3 ? "#bfefff" : "#e8fbff", 0.15),
          0.9,
        );
      }
    } else if (k === GROUND.ruin) {
      // The old garrison: a stub of wall along one edge of the cell, a fallen block, now and then a column drum.
      const yaw = Math.floor(rnd() * 6) * (Math.PI / 3);
      const ox = Math.cos(yaw) * S * 0.36;
      const oz = -Math.sin(yaw) * S * 0.36;
      const hh = 0.12 + rnd() * 0.22;
      put(
        stones,
        cx + ox,
        h + hh / 2,
        cz + oz,
        0.12,
        hh,
        S * 0.5,
        tint("#575a5b", 0.15),
        0.04,
        yaw,
      );
      put(
        stones,
        cx + jit(0.5),
        h + 0.04,
        cz + jit(0.5),
        0.14,
        0.08,
        0.1,
        tint("#4d5051", 0.2),
        0.4,
      );
      if (rnd() < 0.35)
        put(
          rocks,
          cx + jit(0.4),
          h + 0.05,
          cz + jit(0.4),
          0.11,
          0.09,
          0.11,
          tint("#6a6b6a", 0.15),
          0.2,
        );
    } else if (k === GROUND.open && hexLen(q, r) > 1 && !low && rnd() < 0.3) {
      for (let i = 0; i < 2; i++)
        put(
          tufts,
          cx + jit(0.7),
          h,
          cz + jit(0.7),
          0.06 + rnd() * 0.04,
          0.1 + rnd() * 0.1,
          0.06 + rnd() * 0.04,
          tint("#4b5631", 0.3),
          0.3,
        );
    }
  }
  // Every kind gets its mesh, even with nothing to draw (with instance colours), so a later rebuild never makes a
  // new one; a rebuild keeps the old material and geometry.
  const prev = (name: string): THREE.InstancedMesh | null =>
    (old?.getObjectByName(name) as THREE.InstancedMesh | undefined) ?? null;
  const add = (geo: THREE.BufferGeometry, list: Item[], name: string): void => {
    const was = prev(name);
    const im = new THREE.InstancedMesh(
      was?.geometry ?? geo,
      (was?.material as THREE.Material | undefined) ??
        new THREE.MeshStandardMaterial({
          roughness: 0.95,
          metalness: 0,
          flatShading: true,
        }),
      Math.max(1, list.length),
    );
    if (was) geo.dispose();
    im.count = list.length;
    if (list.length === 0) im.setColorAt(0, new THREE.Color());
    im.name = name;
    list.forEach((it, i) => {
      im.setMatrixAt(i, it.m);
      im.setColorAt(i, it.c);
    });
    im.instanceMatrix.needsUpdate = true;
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
    im.computeBoundingSphere();
    g.add(im);
  };
  add(new THREE.CylinderGeometry(0.5, 0.75, 1, 5), trunks, "land-trunks");
  add(new THREE.ConeGeometry(1, 1, 7), cones, "land-cones");
  add(new THREE.DodecahedronGeometry(1, 0), rocks, "land-rocks");
  add(new THREE.BoxGeometry(1, 1, 1), stones, "land-stones");
  add(
    new THREE.ConeGeometry(0.5, 1, 4).translate(0, 0.5, 0),
    tufts,
    "land-tufts",
  );
  // The veins' crystal lights itself a little, so a vein reads from across the land (no light of its own).
  {
    const was = prev("land-shards");
    const im = new THREE.InstancedMesh(
      was?.geometry ?? new THREE.OctahedronGeometry(0.5, 0).scale(1, 1.6, 1),
      (was?.material as THREE.Material | undefined) ??
        new THREE.MeshStandardMaterial({
          roughness: 0.25,
          metalness: 0.1,
          flatShading: true,
          emissive: new THREE.Color("#3fb8d8"),
          emissiveIntensity: 0.55,
        }),
      Math.max(1, shards.length),
    );
    im.count = shards.length;
    if (shards.length === 0) im.setColorAt(0, new THREE.Color());
    im.name = "land-shards";
    shards.forEach((it, i) => {
      im.setMatrixAt(i, it.m);
      im.setColorAt(i, it.c);
    });
    im.instanceMatrix.needsUpdate = true;
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
    im.computeBoundingSphere();
    g.add(im);
  }
  return g;
};

/** Ground height under a sim point (fixed coords), following the lean of the cell's top. */
export const landHeightAt = (
  b: LandBuild,
  map: MapDef,
  x: number,
  y: number,
): number => {
  const qf = (x - (y * 1000) / 866 / 2) / 1000;
  const rf = y / 866;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(-qf - rf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - (-qf - rf));
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  const id = cellId(map.radius, q, r);
  if (id < 0) return BASE;
  const c = hexToWorld(q, r);
  const px = ((x - c.x) / 1000) * S;
  const pz = ((y - c.y) / 1000) * S;
  // Which slice of the fan the point lies in, then barycentric on that triangle.
  const deg = (Math.atan2(pz, px) * 180) / Math.PI;
  const j = ((Math.floor((deg + 30) / 60) % 6) + 6) % 6;
  const j2 = (j + 1) % 6;
  const ax = (CORNER[j] as [number, number])[0];
  const az = (CORNER[j] as [number, number])[1];
  const bx = (CORNER[j2] as [number, number])[0];
  const bz = (CORNER[j2] as [number, number])[1];
  const den = ax * bz - bx * az;
  const u = (px * bz - bx * pz) / den;
  const v = (ax * pz - px * az) / den;
  const w = 1 - u - v;
  return (
    w * (b.heights[id] as number) +
    u * (b.corners[id * 6 + j] as number) +
    v * (b.corners[id * 6 + j2] as number)
  );
};

/** A thin jagged crack lying on the ground, along X, painted `color`. */
const crackGeo = (
  rnd: () => number,
  len: number,
  wide: number,
  color: string,
): THREE.BufferGeometry => {
  const top: [number, number][] = [];
  const bot: [number, number][] = [];
  let off = 0;
  const n = 7;
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const w =
      wide * (0.35 + 0.65 * Math.sin(Math.PI * t)) * (0.6 + rnd() * 0.8) +
      0.003;
    off += (rnd() - 0.5) * len * 0.09;
    const x = -len / 2 + t * len;
    top.push([x, off + w]);
    bot.push([x, off - w]);
  }
  const shape = new THREE.Shape();
  shape.moveTo(
    (top[0] as [number, number])[0],
    (top[0] as [number, number])[1],
  );
  for (const [x, y] of top.slice(1)) shape.lineTo(x, y);
  for (const [x, y] of bot.reverse()) shape.lineTo(x, y);
  const geo = new THREE.ShapeGeometry(shape).rotateX(-Math.PI / 2);
  const col = new THREE.Color(color);
  const cols: number[] = [];
  for (
    let i = 0;
    i < (geo.attributes.position as THREE.BufferAttribute).count;
    i++
  )
    cols.push(col.r, col.g, col.b);
  geo.setAttribute("color", new THREE.Float32BufferAttribute(cols, 3));
  return geo;
};

/**
 * An open fissure (from the open-world trial): a long jagged lens sunk into the ground. The broken lip, the
 * black throat, a seam of embers deep in it, cracks running off and rubble heaved up along the edges.
 */
export const buildRift = (seed: number, low: boolean): THREE.Group => {
  const rnd = mulberry(seed);
  const g = new THREE.Group();
  g.name = "rift";
  const L = 1.5;
  const lens = (
    len: number,
    wide: number,
    y: number,
    mat: THREE.Material,
    jag: number,
  ): THREE.Mesh => {
    const n = 12;
    const pts: [number, number][] = [];
    for (let i = 0; i <= n; i++)
      pts.push([
        -len / 2 + (i / n) * len,
        wide *
          Math.pow(Math.sin((Math.PI * i) / n), 0.8) *
          (1 + (rnd() - 0.5) * jag),
      ]);
    for (let i = n - 1; i > 0; i--)
      pts.push([
        -len / 2 + (i / n) * len,
        -wide *
          Math.pow(Math.sin((Math.PI * i) / n), 0.8) *
          (1 + (rnd() - 0.5) * jag),
      ]);
    const shape = new THREE.Shape();
    shape.moveTo(
      (pts[0] as [number, number])[0],
      (pts[0] as [number, number])[1],
    );
    for (const [x, z] of pts.slice(1)) shape.lineTo(x, z);
    const m = new THREE.Mesh(
      new THREE.ShapeGeometry(shape).rotateX(-Math.PI / 2),
      mat,
    );
    m.position.y = y;
    return m;
  };
  g.add(
    lens(
      L * 1.15,
      0.4,
      0.01,
      new THREE.MeshStandardMaterial({
        color: "#3a3330",
        roughness: 1,
        flatShading: true,
      }),
      0.7,
    ),
  );
  g.add(
    lens(
      L,
      0.27,
      0.016,
      new THREE.MeshBasicMaterial({ color: "#0a0708" }),
      0.8,
    ),
  );
  g.add(
    lens(
      L * 0.78,
      0.05,
      0.022,
      new THREE.MeshBasicMaterial({ color: "#a32a1a" }),
      0.9,
    ),
  );
  g.add(
    lens(
      L * 0.55,
      0.016,
      0.028,
      new THREE.MeshBasicMaterial({ color: "#ff8a6e" }),
      0.9,
    ),
  );
  const cm = new THREE.MeshBasicMaterial({ vertexColors: true });
  for (let i = 0; i < 6; i++) {
    const geo = crackGeo(
      rnd,
      0.5 + rnd() * 0.5,
      0.016,
      i % 2 ? "#4a1a17" : "#1a1513",
    );
    const end = i < 2;
    const side = end ? (i === 0 ? 1 : -1) : rnd() < 0.5 ? 1 : -1;
    const a = end
      ? (side > 0 ? 0 : Math.PI) + (rnd() - 0.5) * 0.6
      : side * (Math.PI / 2 + (rnd() - 0.5) * 0.8);
    geo.translate(0.25, 0, 0);
    geo.rotateY(-a);
    geo.translate(
      end ? side * L * 0.55 : (rnd() - 0.5) * L * 0.7,
      0.02,
      end ? 0 : side * 0.25,
    );
    g.add(new THREE.Mesh(geo, cm));
  }
  if (!low) {
    const rock = new THREE.MeshStandardMaterial({
      color: "#3b3533",
      roughness: 1,
      flatShading: true,
    });
    const geo = new THREE.DodecahedronGeometry(1, 0);
    for (let i = 0; i < 9; i++) {
      const t = rnd() - 0.5;
      const side = rnd() < 0.5 ? 1 : -1;
      const m = new THREE.Mesh(geo, rock);
      const sc = 0.05 + rnd() * 0.07;
      m.scale.set(sc * 1.3, sc * 0.8, sc);
      m.rotation.set(rnd() * 3, rnd() * 3, rnd() * 3);
      m.position.set(
        t * L,
        sc * 0.4,
        side * (0.36 + rnd() * 0.12) * Math.sin(Math.PI * (t + 0.5)),
      );
      g.add(m);
    }
  }
  g.rotation.y = rnd() * Math.PI;
  return g;
};

const PLINTH_TOP = new THREE.MeshStandardMaterial({
  color: "#4f4b45",
  roughness: 0.95,
  flatShading: true,
});
const PLINTH_SIDE = new THREE.MeshStandardMaterial({
  color: "#35322e",
  roughness: 1,
  flatShading: true,
});

/**
 * The dressed-stone plinth a three-cell building stands on, one hex slab per cell (world size: a hex is
 * WORLD_PER_HEX across its flats), its top a shade lighter than its sides so the footprint reads from the table.
 */
export const plinthModel = (): {
  root: THREE.Group;
  tick: () => void;
  height: number;
} => {
  const root = new THREE.Group();
  const r = (WORLD_PER_HEX / Math.sqrt(3)) * 0.97;
  const side = new THREE.Mesh(
    new THREE.CylinderGeometry(r, r * 1.02, 0.06, 6),
    PLINTH_SIDE,
  );
  side.position.y = 0.02;
  root.add(side);
  const top = new THREE.Mesh(
    new THREE.CylinderGeometry(r * 0.93, r * 0.93, 0.015, 6),
    PLINTH_TOP,
  );
  top.position.y = 0.055;
  root.add(top);
  return { root, tick: () => undefined, height: 0.06 };
};
